import React, { ComponentType, FC } from 'react';
import createSagaMiddleware, { Saga, SagaMiddleware } from 'redux-saga';
import hoistNonReactStatics from 'hoist-non-react-statics';

import { middlewareFactories, storeDecorators } from '../withStore';

export const sagaMiddlewareKey = Symbol('sagaMiddleware');

export interface WithSagaProps {}
export interface WithSagaOptions {
  sagas?: Saga[];
}

type WithSagaType = (
  (<T extends {}>(WrappedComponent: ComponentType<T>) => ComponentType<T & WithSagaProps>) &
  ((options: WithSagaOptions) => <T extends {}>(WrappedComponent: ComponentType<T>) => ComponentType<T & WithSagaProps>)
);

export interface WithSagaMock {}

/**
 * @since 0.3.0
 * @see See [Redux-Saga](https://redux-saga.js.org/)
 * @see See [App configuration](/Devguide/App%20configuration)
 * @component
 */
function withSaga<T>(WrappedComponentOrConfig: ComponentType<T> | WithSagaOptions) {
  if (typeof WrappedComponentOrConfig === 'function' || (WrappedComponentOrConfig as any).type || (WrappedComponentOrConfig as any).render) {
    return withSagaConfig(WrappedComponentOrConfig as FC<WithSagaProps>);
  }
  return (WrappedComponent: FC<WithSagaProps>) => withSagaConfig(WrappedComponent, WrappedComponentOrConfig);

  // This approach using the middlewareFactories is for the styleguide where multiple instances of redux are created
  function withSagaConfig(WrappedComponent: FC<WithSagaProps>, { sagas = [] }: WithSagaOptions = {}) {
    middlewareFactories.push(() => {
      const sagaMiddleware = createSagaMiddleware();
      (sagaMiddleware as any)[sagaMiddlewareKey] = true;
      return sagaMiddleware;
    });

    storeDecorators.push((store) => {
      const sagaMiddleware = store.middlewares.find((middleware) => (middleware as any)[sagaMiddlewareKey]) as SagaMiddleware;

      Object.assign(store, {
        // eslint-disable-next-line @typescript-eslint/unbound-method
        runSaga: sagaMiddleware.run,
        injectedSagas: {},
      });

      sagas.forEach((saga) => {
        store.runSaga(saga);
      });
    });

    const WithSaga = (props: WithSagaProps) => (
      <WrappedComponent {...props} />
    );

    return hoistNonReactStatics(WithSaga, WrappedComponent);
  }
}

export default withSaga as WithSagaType;
